import { useState, useEffect, useCallback } from "react";
import { Bell } from "lucide-react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useSearchParams } from "react-router-dom";

function getInitials(email?: string): string {
  if (!email) return "?";
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function timeAgo(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `hace ${days}d`;
}

interface Notification {
  id: string;
  tipo: string;
  titulo: string;
  mensaje: string | null;
  leido: boolean;
  actor_id: string;
  metadata: any;
  created_at: string;
}

function useNotifications() {
  const { user, orgId } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["notificaciones", user?.id];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("notificaciones")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as Notification[];
    },
    enabled: !!user,
  });

  // Realtime
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`notif-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notificaciones",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, queryClient, queryKey]);

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notificaciones")
        .update({ leido: true })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  return { notifications: query.data ?? [], isLoading: query.isLoading, markRead };
}

export function NotificationBell() {
  const { notifications, markRead } = useNotifications();
  const { data: orgMembers = [] } = useOrgMembersLite();
  const [open, setOpen] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const unreadCount = notifications.filter((n) => !n.leido).length;

  const handleClick = useCallback((n: Notification) => {
    if (!n.leido) markRead.mutate(n.id);
    setOpen(false);

    // Navigate to entity if metadata has shipment info
    const meta = n.metadata as any;
    if (meta?.entityType === "embarque" && meta?.entityId) {
      setSearchParams({ shipmentId: meta.entityId, showComments: "true" });
    }
  }, [markRead, setSearchParams]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-sm font-semibold">Notificaciones</p>
        </div>
        <ScrollArea className="max-h-[360px]">
          {notifications.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Sin notificaciones
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((n) => {
                const actor = orgMembers.find((m) => m.user_id === n.actor_id);
                const actorEmail = actor?.email ?? n.actor_id.slice(0, 8);
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                      !n.leido ? "bg-primary/5" : ""
                    }`}
                  >
                    <Avatar className="h-7 w-7 shrink-0 mt-0.5">
                      <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                        {getInitials(actorEmail)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{n.titulo}</p>
                      {n.mensaje && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {n.mensaje}
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {actorEmail.includes("@") ? actorEmail.split("@")[0] : actorEmail} · {timeAgo(n.created_at)}
                      </p>
                    </div>
                    {!n.leido && (
                      <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-2" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

// Lightweight member lookup for actor emails
function useOrgMembersLite() {
  const { orgId } = useAuth();
  return useQuery({
    queryKey: ["org-members", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase.rpc("get_org_members_with_email", { _org_id: orgId });
      if (error) throw error;
      return (data ?? []) as { user_id: string; email: string; rol: string }[];
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}
